<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcWorksheetData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    worksheet: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadonly = computed(() => {
    return false;
  });

  watch(
    () => props.worksheet,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('worksheet');
    }
  );

  const act_7315092382398562_reloadComponentData_ksqmm7i35 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_znngn3de8 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ksqmm7i35,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_znngn3de8],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_znngn3de8,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_znngn3de8 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_znngn3de8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="container p-0">
      <ItpRow class="g-0">
        <ItpCol :w="6">
          <ItpRow class="g-0 mb-2" v-if="worksheet.status">
            <ItpText
              :text="pgettext('Worksheet', 'Status')"
              muted
              class="col-3"
            >
            </ItpText>
            <UcWorksheetStatus strong :value="worksheet.status" class="col">
            </UcWorksheetStatus>
          </ItpRow>
          <ItpRow class="g-0 mb-2" v-if="worksheet.company">
            <ItpText
              :text="pgettext('Worksheet', 'Own company')"
              muted
              class="col-3"
            >
            </ItpText>
            <UcCompanyMiniCard :item="worksheet.company" no-icon class="col">
            </UcCompanyMiniCard>
          </ItpRow>
          <ItpRow class="g-0 mb-2" v-if="worksheet.client">
            <ItpText
              :text="pgettext('Worksheet', 'Partner')"
              muted
              class="col-3"
            >
            </ItpText>
            <UcCompanyMiniCard :item="worksheet.client" no-icon class="col">
            </UcCompanyMiniCard>
          </ItpRow>
          <ItpRow class="g-0 mb-2" v-if="worksheet.contractor">
            <ItpText
              :text="pgettext('Worksheet', 'Contractor')"
              muted
              class="col-3"
            >
            </ItpText>
            <UcCompanyMiniCard :item="worksheet.contractor" no-icon class="col">
            </UcCompanyMiniCard>
          </ItpRow>
          <ItpRow class="g-0 mb-2" v-if="worksheet.rental">
            <ItpText
              :text="pgettext('Worksheet', 'Rental')"
              muted
              class="col-3"
            >
            </ItpText>
            <UcRentalMiniCard no-icon :item="worksheet.rental" class="col">
            </UcRentalMiniCard>
          </ItpRow>
        </ItpCol>
        <ItpCol :w="6">
          <ItpRow class="g-0 mb-2" v-if="worksheet.description">
            <ItpText
              :text="pgettext('Worksheet', 'Description')"
              muted
              class="col-3"
            >
            </ItpText>
            <ItpText :text="worksheet.description" strong class="col-6">
            </ItpText>
          </ItpRow>
          <ItpRow class="g-0 mb-2" v-if="worksheet.worksheet_date">
            <ItpText :text="pgettext('Worksheet', 'Date')" muted class="col-3">
            </ItpText>
            <ItpText
              :text="date(worksheet.worksheet_date)"
              strong
              class="col-6"
            >
            </ItpText>
          </ItpRow>
          <ItpRow
            class="g-0 mb-2"
            v-if="worksheet.start_date || worksheet.end_date"
          >
            <ItpText
              :text="pgettext('Worksheet', 'Start/End')"
              muted
              class="col-3"
            >
            </ItpText>
            <div class="col-6">
              <ItpText
                :text="date(worksheet.start_date)"
                defaultText="n/a"
                strong
              >
              </ItpText>
              <span> &#8208; </span>
              <ItpText
                :text="date(worksheet.end_date)"
                defaultText="n/a"
                strong
              >
              </ItpText>
            </div>
          </ItpRow>
          <ItpRow class="g-0 mb-2" v-if="worksheet.gl_account_number">
            <ItpText
              :text="pgettext('Worksheet', 'GL Account Number')"
              muted
              class="col-3"
            >
            </ItpText>
            <ItpText
              :text="worksheet.gl_account_number"
              wrap
              strong
              class="col-9"
            >
            </ItpText>
          </ItpRow>
          <ItpRow class="g-0 mb-2" v-if="worksheet.notes">
            <ItpText :text="pgettext('Worksheet', 'Notes')" muted class="col-3">
            </ItpText>
            <ItpText :text="worksheet.notes" wrap class="col-9"> </ItpText>
          </ItpRow>
        </ItpCol>
      </ItpRow>
    </div>
  </ItpUsercontrol>
</template>
